import React from "react";
import { Star } from "lucide-react";
import { PageHeader, Panel, State } from "../components/terminal/Page";
import Sparkline from "../components/terminal/Sparkline";
import { useMarket } from "../context/MarketContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { meta, fmtPrice } from "../lib/symbols";
import { SymbolCatalog } from "../engine";

// /watchlist — the persisted watchlist (felacoo_watchlists) fed by the shared tick bus. Bid/ask only when the feed supplies them.
export default function WatchlistPage() {
  const { quotes, feed } = useMarket();
  const { favorites, toggleFav, openChart, catalogReady } = useWorkspace();
  return (
    <div className="space-y-3" data-testid="page-watchlist">
      <PageHeader title="Watchlist" subtitle={`${favorites.length} instruments · click a row to open it in the chart`} />
      <Panel testid="wl-table" className="overflow-hidden">
        <State loading={!catalogReady && !favorites.length} empty={!favorites.length} emptyText="Your watchlist is empty. Star instruments on the Markets page." testid="wl">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr className="text-left"><th className="py-1.5 w-6"></th><th>Symbol</th><th className="text-right">Price</th><th className="text-right">Change</th><th className="text-right">Bid</th><th className="text-right">Ask</th><th className="text-right">Spread</th><th>Market status</th><th>Last update</th><th className="text-right">Trend</th></tr>
            </thead>
            <tbody>
              {favorites.map((code) => {
                const q = quotes[code], m = meta(code), cat = SymbolCatalog.get(code);
                const open = cat ? cat.exchange_is_open !== false && !cat.is_trading_suspended : true;
                return (
                  <tr key={code} data-testid={`wl-row-${code}`} onClick={() => openChart(code)} className="border-t border-border hover:bg-secondary/60 cursor-pointer">
                    <td className="py-2"><button data-testid={`wl-star-${code}`} onClick={(e) => { e.stopPropagation(); toggleFav(code); }} className="text-primary"><Star className="h-3.5 w-3.5 fill-current" /></button></td>
                    <td><div className="font-semibold">{m.display}</div><div className="text-[10px] text-muted-foreground">{m.name}</div></td>
                    <td className="text-right font-mono-nums">{q?.price != null ? fmtPrice(q.price, m.decimals) : "—"}</td>
                    <td className={`text-right font-mono-nums ${q ? (q.change >= 0 ? "text-buy" : "text-sell") : "text-muted-foreground"}`}>{q ? `${q.change >= 0 ? "+" : ""}${fmtPrice(q.change, m.decimals)} (${q.changePct >= 0 ? "+" : ""}${q.changePct.toFixed(2)}%)` : "—"}</td>
                    <td className="text-right font-mono-nums text-muted-foreground">{q?.bid != null ? fmtPrice(q.bid, m.decimals) : "—"}</td>
                    <td className="text-right font-mono-nums text-muted-foreground">{q?.ask != null ? fmtPrice(q.ask, m.decimals) : "—"}</td>
                    <td className="text-right font-mono-nums text-muted-foreground">{q?.bid != null && q?.ask != null ? fmtPrice(q.ask - q.bid, m.decimals) : "—"}</td>
                    <td><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${open ? "bg-buy/15 text-buy" : "bg-secondary text-muted-foreground"}`}>{open ? (feed.mode === "stream" ? "live" : "open") : "closed"}</span></td>
                    <td className="text-muted-foreground font-mono-nums">{q?.at ? new Date(q.at).toLocaleTimeString() : q ? "streaming" : "—"}</td>
                    <td className="text-right">{q?.history?.length > 1 && <Sparkline data={q.history} up={q.change >= 0} width={64} height={20} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </State>
      </Panel>
    </div>
  );
}
